import * as React from "react";

import { cn } from "@src/shared/lib/utils";

import type { WizardContextValue, WizardProps } from "./Wizard.types";
import { WizardContext } from "./WizardContext";

export const Wizard = ({
  steps,
  value,
  defaultValue,
  onValueChange,
  onComplete,
  onSkip,
  className,
  children,
  ...props
}: WizardProps) => {
  if (steps.length === 0) {
    throw new Error("Wizard requires at least one step.");
  }

  const [uncontrolledValue, setUncontrolledValue] = React.useState(
    defaultValue ?? steps[0].value,
  );
  const currentValue = value ?? uncontrolledValue;
  const activeStepIndex = Math.max(
    steps.findIndex((step) => step.value === currentValue),
    0,
  );
  const activeStep = steps[activeStepIndex];

  const setActiveValue = React.useCallback(
    (nextValue: string) => {
      if (!steps.some((step) => step.value === nextValue)) {
        return;
      }

      if (value === undefined) {
        setUncontrolledValue(nextValue);
      }

      onValueChange?.(nextValue);
    },
    [onValueChange, steps, value],
  );

  const context = React.useMemo<WizardContextValue>(() => {
    const isFirstStep = activeStepIndex === 0;
    const isLastStep = activeStepIndex === steps.length - 1;

    return {
      steps,
      activeStep,
      activeStepIndex,
      totalSteps: steps.length,
      isFirstStep,
      isLastStep,
      goToStep: setActiveValue,
      goNext: () => {
        if (isLastStep) {
          onComplete?.();
          return;
        }

        setActiveValue(steps[activeStepIndex + 1].value);
      },
      goBack: () => {
        if (isFirstStep) {
          return;
        }

        setActiveValue(steps[activeStepIndex - 1].value);
      },
      skip: () => {
        onSkip?.();
      },
    };
  }, [activeStep, activeStepIndex, onComplete, onSkip, setActiveValue, steps]);

  return (
    <WizardContext.Provider value={context}>
      <div
        className={cn(
          "mx-auto flex w-full max-w-[460px] flex-col items-center px-6 pb-page-y pt-[26px] text-center",
          className,
        )}
        {...props}
      >
        {children}
      </div>
    </WizardContext.Provider>
  );
};
